import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';

const FilterSlidePanel = ({
  open,
  onClose,
  title = "Filtros",
  children,
  onApply,
  onCancel,
  onSave,
  onSaveAndApply,
  showApplyButton = true,
  showCancelButton = true,
  showSaveButton = false,
  applyButtonText = "Aplicar",
  cancelButtonText = "Cancelar",
  saveButtonText = "Salvar",
  saveAndApplyButtonText = "Salvar e Aplicar",
  width = 400,
  selectedFilter = null,
  canSave = false,
  isCreating = false,
  hasChanges = false
}) => {
  const { theme } = React.useContext(ThemeContext);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1200, // Acima dos FABs que geralmente estão em z-index 1050-1100
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '90%', md: width },
          maxWidth: width,
          backgroundColor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15)'
        },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        zIndex: 1
      }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      {/* Content */}
      <Box sx={{ 
        p: { xs: 1, sm: 2 }, 
        flex: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0 // Permite que o conteúdo seja scrollável
      }}>
        {children}
      </Box>
      
      {/* Footer with action buttons */}
      {(showApplyButton || showCancelButton || showSaveButton) && (
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          justifyContent: 'center',
          flexShrink: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          flexWrap: 'wrap',
          '& > button': {
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            minWidth: { xs: 'calc(50% - 4px)', sm: 100 }
          }
        }}>
          {showCancelButton && (
            <Button 
              variant="outlined" 
              onClick={onCancel || onClose}
              sx={{ 
                minWidth: { xs: 'auto', sm: 100 },
                order: 1
              }}
            >
              {cancelButtonText}
            </Button>
          )}
          {/* Botão Salvar (apenas quando está criando um novo filtro) */}
          {showSaveButton && canSave && isCreating && onSave && (
            <Button 
              variant="contained" 
              color="secondary"
              onClick={onSave}
              sx={{ 
                minWidth: { xs: 'auto', sm: 120 },
                order: 2
              }}
            >
              {saveButtonText}
            </Button>
          )}
          {/* Botão Salvar e Aplicar (quando há alterações em um filtro existente) */}
          {showSaveButton && canSave && hasChanges && !isCreating && onSaveAndApply && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={onSaveAndApply}
              sx={{ 
                minWidth: { xs: 'auto', sm: 150 },
                order: 2
              }}
            >
              {saveAndApplyButtonText}
            </Button>
          )}
          {/* Botão Aplicar (apenas quando não há alterações) */}
          {showApplyButton && selectedFilter && !hasChanges && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={onApply}
              sx={{ 
                minWidth: { xs: 'auto', sm: 120 },
                order: 3
              }}
            >
              {applyButtonText}
            </Button>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default FilterSlidePanel;
